/**
 * Resolving coupled (`mimic`) joints: what order to evaluate them in, and what
 * value each one lands at.
 *
 * Pure arithmetic over the declared articulation, with no Babylon and no ECS, so
 * the two things that are easy to get wrong — the dependency order and the
 * clamping rule — are testable on their own. `poseJoints` is then the thin part:
 * take the values these produce and write TRS.
 *
 * Lives beside `entityHierarchy`, and for the same reason: both interpret a
 * graph of references declared in the schema, and both have to keep working on a
 * malformed one rather than throwing.
 */

import type { Joint } from "./three-d-schemas";

/**
 * The joints of a model, ordered so a coupled joint follows the one it copies.
 *
 * `order` is what an evaluator iterates: every joint whose value can be
 * computed, with each mimic placed after its driver, so a chain of couplings
 * resolves transitively in a single pass. Independent joints keep their declared
 * order among themselves.
 *
 * `unresolvable` names the joints left out, and is the reason to hand this back
 * rather than silently shortening the list: the settings UI has to say why a
 * joint offers no control, and "it is part of a cycle" is a statement about the
 * model rather than about the panel.
 */
export interface MimicOrder {
  order: Joint[];
  unresolvable: Set<string>;
}

/**
 * The joints in dependency order, with the unresolvable ones separated out.
 *
 * A depth-first walk with colour marking, mirroring `findParentCycle`: a joint
 * being visited is grey, a finished one is black, and reaching a grey joint
 * means the walk has come back round to a joint it is already inside — a cycle.
 *
 * Three things make a joint unresolvable, and they are the same thing seen from
 * different sides: it mimics a joint that is **in a cycle**, it mimics a joint
 * that is **not in the list** (a driver dropped for being malformed, or a name
 * that matches nothing), or it mimics a joint that is **itself unresolvable**.
 * All three mean `multiplier * driver + offset` has no driver to read, so the
 * joint is excluded rather than evaluated against a missing value — which would
 * make it `NaN` and take its whole subtree with it.
 *
 * An excluded joint keeps whatever transform the file gave it, which for a
 * `ROBOTO_device` export is the zero pose. That is the closest thing to "no
 * claim about where this joint is" the format has.
 *
 * Two joints of the same name would be ambiguous to address, so the first wins
 * and the rest are unresolvable — the same "first one wins" rule the import uses
 * for duplicate node names.
 */
export function mimicOrder(joints: readonly Joint[]): MimicOrder {
  const byName = new Map<string, Joint>();
  const duplicates: Joint[] = [];
  for (const joint of joints) {
    if (byName.has(joint.name)) {
      duplicates.push(joint);
    } else {
      byName.set(joint.name, joint);
    }
  }

  const order: Joint[] = [];
  const unresolvable = new Set<string>(
    duplicates.map((duplicate) => duplicate.name),
  );
  /** Grey while a joint's driver chain is being walked, black once settled. */
  const state = new Map<string, "visiting" | "resolved" | "unresolvable">();

  /** @returns whether this joint's value can be computed. */
  const visit = (joint: Joint): boolean => {
    const seen = state.get(joint.name);
    if (seen === "visiting") {
      // Back round to a joint this walk is already inside: a cycle, of any
      // length including one (a joint mimicking itself).
      return false;
    }
    if (seen !== undefined) {
      return seen === "resolved";
    }

    state.set(joint.name, "visiting");
    const driverName = joint.mimic?.joint;
    if (driverName !== undefined) {
      const driver = byName.get(driverName);
      if (driver === undefined || !visit(driver)) {
        state.set(joint.name, "unresolvable");
        unresolvable.add(joint.name);
        return false;
      }
    }
    state.set(joint.name, "resolved");
    order.push(joint);
    return true;
  };

  for (const joint of joints) {
    if (byName.get(joint.name) === joint) {
      visit(joint);
    }
  }

  return { order, unresolvable };
}

/**
 * The value each joint sits at, given what the independently driven ones are at.
 *
 * `order` must come from {@link mimicOrder}, because the single pass depends on
 * a mimic's driver having been evaluated already.
 *
 * `driven` supplies the joints something is actually driving, keyed by joint
 * name and already scaled into the joint's own units. A joint absent from it
 * reads as 0 — the pose a `ROBOTO_device` file is exported in — so a model with
 * nothing bound evaluates to exactly the transforms the file describes.
 *
 * ## What this function clamps, and what it leaves to its caller
 *
 * A mimic's value is computed here, so putting it outside the joint's own limits
 * would be this function inventing a pose the robot cannot reach — clamping is
 * the fix, and it is applied to derived values only.
 *
 * Every number in `driven` arrives already resolved *and already constrained if
 * it needed to be*, because whether it needed to be depends on where it came
 * from, which this function cannot see. A value someone **authored** — a slider
 * pose, a constant typed into a joint's field — must not be able to put a joint
 * outside its declared travel, and `drivenValues` holds it inside. A value
 * **recorded** into a topic is an observation, and observations are reported as
 * recorded: a real robot's joint really can sit a couple of percent past the
 * limit its URDF declares, and clamping that away deletes the discrepancy along
 * with the evidence for it. It also deletes the loudest signal a unit mistake
 * has: an unclamped joint fed degrees as radians visibly spins.
 *
 * ## What a mimic derives from, when its driver can be either
 *
 * Its driver's *resolved* value — the same number the driver's own link is posed
 * at, whichever way the driver got it. There is one rule, not two: a follower
 * is consistent with where its driver is drawn. The follower's own limits then
 * clamp the result as they always have, so a coupling never leaves the
 * follower's declared travel even where its driver has left its own.
 */
export function evaluateJointValues(
  order: readonly Joint[],
  driven: Readonly<Record<string, number>>,
): Record<string, number> {
  const values: Record<string, number> = {};
  for (const joint of order) {
    const mimic = joint.mimic;
    if (mimic === undefined) {
      values[joint.name] = driven[joint.name] ?? 0;
      continue;
    }
    values[joint.name] = clampToLimits(
      (values[mimic.joint] ?? 0) * mimic.multiplier + mimic.offset,
      joint,
    );
  }
  return values;
}

/**
 * `value` brought inside the joint's declared travel, where it declares any.
 *
 * A `continuous` joint turns without end, so whatever limits it carries do not
 * bound it. Inverted limits (`lower > upper`) bound nothing either — there is no
 * interval to clamp into, and picking one of the two ends would be a guess.
 *
 * Each bound is applied on its own, so a joint declaring only one of the two is
 * held against the one it declares rather than left free — the file said where
 * that end of the travel is, and inventing the other end to make a pair would be
 * the guess, not honouring the half that exists.
 *
 * `lower === upper` needs no case of its own: the range holds exactly one value,
 * and applying both bounds lands on it. Nothing here divides by the range, so a
 * pinned joint yields its one value rather than a `NaN`.
 */
export function clampToLimits(value: number, joint: Joint): number {
  const { lower, upper } = joint;
  if (joint.type === "continuous") {
    return value;
  }
  if (lower !== undefined && upper !== undefined && lower > upper) {
    return value;
  }
  let clamped = value;
  if (lower !== undefined) {
    clamped = Math.max(clamped, lower);
  }
  if (upper !== undefined) {
    clamped = Math.min(clamped, upper);
  }
  return clamped;
}
